import { History, Info } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";

import type { PaymentFee, ProviderRailRoute } from "../../types/paymentFees.types";
import { corridorFilterKey } from "../../utils/feeFormat";
import { changeFeed, type FeeChangeRole } from "../../utils/feeHistory";
import { routesCoveredBy, feeScopeOf } from "../../utils/feeMath";
import { FeeChangeTimeline } from "./FeeChangeTimeline";

const PAGE_SIZE = 30;

export interface FeeChangesFeedProps {
  fees: PaymentFee[];
  now: number;
  routes: ProviderRailRoute[];
  isLoading: boolean;
  scopeOrganizationId: string | null;
  exampleAmountFor: (currency: string | null | undefined) => string;
  organizationNames: Record<string, string>;
  authorNames: Record<string, string>;
}

/** Every recorded change, newest first — where a wrong general price shows up first. */
export function FeeChangesFeed({
  fees,
  now,
  routes,
  isLoading,
  scopeOrganizationId,
  exampleAmountFor,
  organizationNames,
  authorNames,
}: FeeChangesFeedProps) {
  const { t } = useTranslations("paymentFees");
  const [role, setRole] = useState<FeeChangeRole | "ALL">("ALL");
  const [corridor, setCorridor] = useState("");
  const [onlyZero, setOnlyZero] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const feed = useMemo(
    () => changeFeed(fees, now, (fee) => exampleAmountFor(fee.currency)),
    [fees, now, exampleAmountFor],
  );

  const corridorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const route of routes) {
      seen.set(corridorFilterKey(route), `${route.country} · ${formatRail(route.rail)} · ${route.currency}`);
    }
    return [
      { value: "", label: t("filter_corridor_all") },
      ...[...seen.entries()].map(([value, label]) => ({ value, label })),
    ];
  }, [routes, t]);

  const filtered = useMemo(() => {
    const corridorRoutes = corridor ? routes.filter((route) => corridorFilterKey(route) === corridor) : null;
    return feed.filter((change) => {
      if (
        scopeOrganizationId &&
        change.fee.organization_id &&
        change.fee.organization_id !== scopeOrganizationId
      ) {
        return false;
      }
      if (role !== "ALL" && change.role !== role) return false;
      if (onlyZero && !(change.isZero && change.role !== "COST")) return false;
      if (
        corridorRoutes &&
        routesCoveredBy(change.fee.fee_kind, feeScopeOf(change.fee), corridorRoutes).length === 0
      ) {
        return false;
      }
      return true;
    });
  }, [feed, scopeOrganizationId, role, onlyZero, corridor, routes]);

  const roleOptions: { value: FeeChangeRole | "ALL"; label: string }[] = [
    { value: "ALL", label: t("feed_role_all") },
    { value: "GENERAL", label: t("feed_role_general") },
    { value: "SPECIAL", label: t("feed_role_special") },
    { value: "COST", label: t("feed_role_cost") },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <ColocaSelect
          id="fee-feed-role"
          aria-label={t("feed_filter_role")}
          className="w-full sm:w-48"
          value={role}
          onValueChange={(value) => {
            setRole(value as FeeChangeRole | "ALL");
            setVisible(PAGE_SIZE);
          }}
          options={roleOptions}
        />
        <ColocaSelect
          id="fee-feed-corridor"
          aria-label={t("filter_corridor_label")}
          className="w-full sm:w-60"
          value={corridor}
          onValueChange={(value) => {
            setCorridor(value);
            setVisible(PAGE_SIZE);
          }}
          options={corridorOptions}
        />
        <ColocaCheckbox checked={onlyZero} onCheckedChange={setOnlyZero} label={t("feed_only_zero")} />
        <span className="ml-auto text-xs text-muted-foreground">
          {t("feed_count", { count: filtered.length })}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((index) => (
            <ColocaSkeleton key={index} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <ColocaEmpty icon={History} title={t("feed_empty_title")} description={t("feed_empty_description")} />
      ) : (
        <div className={cn(colocaSurfaceVariants(), "px-5 py-3")}>
          <FeeChangeTimeline
            changes={filtered.slice(0, visible)}
            organizationNames={organizationNames}
            authorNames={authorNames}
            showScope
            groupByDay
          />
          {filtered.length > visible && (
            <div className="pt-2 pb-1">
              <ColocaButton
                variant="default"
                size="sm"
                onClick={() => setVisible((count) => count + PAGE_SIZE)}
              >
                {t("feed_load_more")}
              </ColocaButton>
            </div>
          )}
        </div>
      )}

      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        {t("history_deleted_note")}
      </p>
    </div>
  );
}
